/**
 * Pipeline contracts
 *
 * Canonical schemas exchanged between the study request, simulation evidence,
 * and report agent stages of the compliance pipeline. Each contract ships a
 * runtime parser that validates `schemaVersion` plus required fields and throws
 * a descriptive Error on mismatch, mirroring `parseGeometryExchange`.
 */

export interface StudyRequest {
  schemaVersion: 'study-request/v1';
  studyId: string;
  source: 'forma-native' | 'engine';
  engine?: string;
  municipalityId: string;
  geometry: { schemaVersion: 'geometry-exchange/v1'; selectedPaths: readonly string[] };
  inputs: Readonly<Record<string, unknown>>;
}

export type EvidenceStatus = 'screening-ready' | 'needs-review' | 'blocked';

export interface EvidenceMetric {
  label: string;
  value: string | number;
  unit?: string;
}

export interface EvidenceFinding {
  code: string;
  severity: 'info' | 'warning' | 'blocker';
  message: string;
}

export interface EvidenceGrid {
  format: string;
  hash: string;
  width?: number;
  height?: number;
  resolution?: number;
}

export interface SimulationEvidence {
  schemaVersion: 'simulation-evidence/v1';
  studyId: string;
  source: 'forma-native' | 'engine' | 'fixture';
  engine?: string;
  status: EvidenceStatus;
  confidence: 'high' | 'medium' | 'low';
  metrics: readonly EvidenceMetric[];
  grids?: readonly EvidenceGrid[];
  findings: readonly EvidenceFinding[];
  missingInputs: readonly string[];
}

export interface ReportAgentPackage {
  schemaVersion: 'report-agent-package/v1';
  municipalityId: string;
  language: 'he' | 'en';
  generatedAt: string;
  project: { name: string; municipalityId: string; timezone: string };
  studies: readonly SimulationEvidence[];
  disclaimer: string;
}

export function parseStudyRequest(value: unknown): StudyRequest {
  const request = assertObject(value, 'StudyRequest');
  const schemaVersion = assertStringField(request, 'schemaVersion', 'StudyRequest');

  if (schemaVersion !== 'study-request/v1') {
    throw new Error(`Unsupported study request schema: ${schemaVersion}`);
  }

  assertStringField(request, 'studyId', 'StudyRequest');

  const source = assertStringField(request, 'source', 'StudyRequest');

  if (source !== 'forma-native' && source !== 'engine') {
    throw new Error(`Unsupported study request source: ${source}`);
  }

  assertOptionalStringField(request, 'engine', 'StudyRequest');
  assertStringField(request, 'municipalityId', 'StudyRequest');

  const geometry = assertObjectField(request, 'geometry', 'StudyRequest');
  const geometrySchemaVersion = assertStringField(
    geometry,
    'schemaVersion',
    'StudyRequest.geometry',
  );

  if (geometrySchemaVersion !== 'geometry-exchange/v1') {
    throw new Error(`Unsupported study request geometry schema: ${geometrySchemaVersion}`);
  }

  assertStringArrayField(geometry, 'selectedPaths', 'StudyRequest.geometry');
  assertObjectField(request, 'inputs', 'StudyRequest');

  return value as StudyRequest;
}

export function parseSimulationEvidence(value: unknown): SimulationEvidence {
  const evidence = assertObject(value, 'SimulationEvidence');
  const schemaVersion = assertStringField(evidence, 'schemaVersion', 'SimulationEvidence');

  if (schemaVersion !== 'simulation-evidence/v1') {
    throw new Error(`Unsupported simulation evidence schema: ${schemaVersion}`);
  }

  assertStringField(evidence, 'studyId', 'SimulationEvidence');

  const source = assertStringField(evidence, 'source', 'SimulationEvidence');

  if (source !== 'forma-native' && source !== 'engine' && source !== 'fixture') {
    throw new Error(`Unsupported simulation evidence source: ${source}`);
  }

  assertOptionalStringField(evidence, 'engine', 'SimulationEvidence');
  assertEvidenceStatusField(evidence, 'status', 'SimulationEvidence');
  assertConfidenceField(evidence, 'confidence', 'SimulationEvidence');

  const metrics = assertArrayField(evidence, 'metrics', 'SimulationEvidence');
  metrics.forEach((metric, index) => {
    const metricRecord = assertObject(metric, `SimulationEvidence.metrics[${index}]`);
    assertStringField(metricRecord, 'label', `SimulationEvidence.metrics[${index}]`);
    assertStringOrNumberField(metricRecord, 'value', `SimulationEvidence.metrics[${index}]`);
    assertOptionalStringField(metricRecord, 'unit', `SimulationEvidence.metrics[${index}]`);
  });

  if (evidence.grids !== undefined) {
    const grids = assertArrayField(evidence, 'grids', 'SimulationEvidence');
    grids.forEach((grid, index) => {
      const gridRecord = assertObject(grid, `SimulationEvidence.grids[${index}]`);
      assertStringField(gridRecord, 'format', `SimulationEvidence.grids[${index}]`);
      assertStringField(gridRecord, 'hash', `SimulationEvidence.grids[${index}]`);
      assertOptionalNumberField(gridRecord, 'width', `SimulationEvidence.grids[${index}]`);
      assertOptionalNumberField(gridRecord, 'height', `SimulationEvidence.grids[${index}]`);
      assertOptionalNumberField(gridRecord, 'resolution', `SimulationEvidence.grids[${index}]`);
    });
  }

  const findings = assertArrayField(evidence, 'findings', 'SimulationEvidence');
  findings.forEach((finding, index) => {
    const findingRecord = assertObject(finding, `SimulationEvidence.findings[${index}]`);
    assertStringField(findingRecord, 'code', `SimulationEvidence.findings[${index}]`);
    assertSeverityField(findingRecord, 'severity', `SimulationEvidence.findings[${index}]`);
    assertStringField(findingRecord, 'message', `SimulationEvidence.findings[${index}]`);
  });

  assertStringArrayField(evidence, 'missingInputs', 'SimulationEvidence');

  return value as SimulationEvidence;
}

export function parseReportAgentPackage(value: unknown): ReportAgentPackage {
  const pkg = assertObject(value, 'ReportAgentPackage');
  const schemaVersion = assertStringField(pkg, 'schemaVersion', 'ReportAgentPackage');

  if (schemaVersion !== 'report-agent-package/v1') {
    throw new Error(`Unsupported report agent package schema: ${schemaVersion}`);
  }

  assertStringField(pkg, 'municipalityId', 'ReportAgentPackage');

  const language = assertStringField(pkg, 'language', 'ReportAgentPackage');

  if (language !== 'he' && language !== 'en') {
    throw new Error(`Unsupported report agent package language: ${language}`);
  }

  assertStringField(pkg, 'generatedAt', 'ReportAgentPackage');

  const project = assertObjectField(pkg, 'project', 'ReportAgentPackage');
  assertStringField(project, 'name', 'ReportAgentPackage.project');
  assertStringField(project, 'municipalityId', 'ReportAgentPackage.project');
  assertStringField(project, 'timezone', 'ReportAgentPackage.project');

  const studies = assertArrayField(pkg, 'studies', 'ReportAgentPackage');
  studies.forEach((study) => {
    parseSimulationEvidence(study);
  });

  assertStringField(pkg, 'disclaimer', 'ReportAgentPackage');

  return value as ReportAgentPackage;
}

export function assertObject(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object.`);
  }

  return value as Record<string, unknown>;
}

export function assertObjectField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): Record<string, unknown> {
  return assertObject(record[field], `${label}.${field}`);
}

export function assertArrayField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): readonly unknown[] {
  const value = record[field];

  if (!Array.isArray(value)) {
    throw new Error(`${label}.${field} must be an array.`);
  }

  return value;
}

export function assertStringField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): string {
  const value = record[field];

  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`${label}.${field} must be a non-empty string.`);
  }

  return value;
}

function assertOptionalStringField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): void {
  if (record[field] !== undefined) {
    assertStringField(record, field, label);
  }
}

function assertStringOrNumberField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): void {
  const value = record[field];
  const isNonEmptyString = typeof value === 'string' && value.length > 0;
  const isFiniteNumber = typeof value === 'number' && Number.isFinite(value);

  if (!isNonEmptyString && !isFiniteNumber) {
    throw new Error(`${label}.${field} must be a non-empty string or finite number.`);
  }
}

export function assertNumberField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): number {
  const value = record[field];

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${label}.${field} must be a finite number.`);
  }

  return value;
}

export function assertOptionalNumberField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): void {
  if (record[field] !== undefined) {
    assertNumberField(record, field, label);
  }
}

export function assertStringArrayField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): void {
  const value = assertArrayField(record, field, label);

  if (!value.every((item) => typeof item === 'string')) {
    throw new Error(`${label}.${field} must contain only strings.`);
  }
}

function assertConfidenceField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): void {
  const value = assertStringField(record, field, label);

  if (value !== 'high' && value !== 'medium' && value !== 'low') {
    throw new Error(`${label}.${field} must be high, medium, or low.`);
  }
}

function assertEvidenceStatusField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): void {
  const value = assertStringField(record, field, label);

  if (value !== 'screening-ready' && value !== 'needs-review' && value !== 'blocked') {
    throw new Error(`${label}.${field} must be screening-ready, needs-review, or blocked.`);
  }
}

function assertSeverityField(record: Record<string, unknown>, field: string, label: string): void {
  const value = assertStringField(record, field, label);

  if (value !== 'info' && value !== 'warning' && value !== 'blocker') {
    throw new Error(`${label}.${field} must be info, warning, or blocker.`);
  }
}
